import type { _NsSeparator, I18nFormatModule, ParseKeys } from "i18next";
import type { IcuFormats } from "i18next-icu";

import i18n from "i18next";
import ICU from "i18next-icu";
import { initReactI18next, useTranslation } from "react-i18next";

import fr from "./locales/fr.json";

type NameSpaces = keyof (typeof resources)["fr"];
export const resources = { fr } as const;
const allNamespaces = [
  "pages",
  "domain",
  "common",
  "navigation",
  "sidebar",
  "errors",
  "user",
  "filters",
  "company",
  "employee",
] as const;

// Named ICU formats — used as `{var, date, fullUtc}` in messages.
// ICU date skeletons cannot carry a timezone, hence these custom formats.
// Exported so the translation validation test can parse messages with the
// exact same formats the runtime uses.
export const icuFormats = {
  date: {
    // ICU's built-in "short" renders a 2-digit year ("11/06/26"); this is
    // deep-merged with the built-in format to restore "11/06/2026"
    short: { year: "numeric" },
    fullUtc: { dateStyle: "full", timeZone: "UTC" },
    longUtc: {
      year: "numeric",
      month: "long",
      day: "numeric",
      timeZone: "UTC",
    },
    shortUtc: {
      year: "numeric",
      month: "numeric",
      day: "numeric",
      timeZone: "UTC",
    },
    mediumUtc: {
      year: "numeric",
      month: "short",
      day: "numeric",
      timeZone: "UTC",
    },
    dateTime: {
      year: "numeric",
      month: "numeric",
      day: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    },
  },
} satisfies IcuFormats;

export async function initI18n() {
  const i18nInstance = await i18n
    // i18next-icu ships outdated typings (declares "3rdParty" instead of the
    // actual "i18nFormat" module type) -> cast to the real module type
    .use(new ICU({ formats: icuFormats }) as unknown as I18nFormatModule)
    .use(initReactI18next)
    .init({
      resources: {
        fr,
      },
      ns: allNamespaces,
      defaultNS: [],
      lng: "fr",
      fallbackLng: "fr",
    });

  return i18nInstance;
}

export const useAllTranslations = () => useTranslation(allNamespaces);

type i18nDomainKey<NS extends NameSpaces> = NS extends unknown
  ? `${NS}${_NsSeparator}${ParseKeys<NS>}`
  : never;
export type i18nKey = i18nDomainKey<NameSpaces>;

export { useTranslation } from "react-i18next";
export type * from "./@types/i18next";
export { i18n };
